import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CalendarBlank, MapPin, Clock, CaretRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { PORTFOLIO, STUDIO_INFO } from "@/data/content";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

export default function CaseStudyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = PORTFOLIO.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = item
      ? `${item.title} — Millionare Mir Studio`
      : "Case Study — Millionare Mir Studio";
  }, [item]);

  if (!item) {
    return (
      <main className="min-h-screen bg-[#050505] text-[#F5F5F0]">
        <Header />
        <section className="px-6 md:px-12 lg:px-24 pt-40 pb-24">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-6">
            // 404
          </p>
          <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">
            Case study <br /> not found.
          </h1>
          <button
            onClick={() => navigate("/")}
            data-testid="case-study-back-home"
            className="mt-10 inline-flex items-center gap-3 border border-white/20 hover:bg-white hover:text-black px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back to home
          </button>
        </section>
        <Footer />
      </main>
    );
  }

  const related = PORTFOLIO.filter((p) => p.id !== item.id).slice(0, 3);

  return (
    <main
      data-testid={`case-study-${item.id}`}
      className="min-h-screen bg-[#050505] text-[#F5F5F0] overflow-x-hidden"
    >
      <Header />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 md:px-12 lg:px-24 border-b border-white/10">
        <Link
          to="/#portfolio"
          data-testid="case-study-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={14} weight="bold" /> All Work
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-5">
              // {item.category}
            </p>
            <h1
              data-testid="case-study-title"
              className="font-display uppercase text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88]"
            >
              {item.title}
            </h1>
            <p className="mt-6 text-zinc-300 text-lg md:text-xl leading-relaxed max-w-2xl">
              {item.subtitle}
            </p>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-px bg-white/10 border border-white/10">
            <Meta icon={CalendarBlank} label="Year" value={item.year} />
            <Meta icon={MapPin} label="Location" value={item.location} />
            <Meta icon={Clock} label="Scope" value={item.duration} />
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9 }}
          className="aspect-[16/9] md:aspect-[16/7] w-full overflow-hidden border-b border-white/10"
        >
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% 35%" }}
          />
        </motion.div>
      </section>

      {/* Story + sidebar */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-6">
              // The Story
            </p>
            <div className="space-y-6 text-zinc-300 text-lg leading-relaxed max-w-3xl">
              {item.story.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Client
              </div>
              <div className="font-display uppercase text-white text-2xl leading-none">
                {item.client}
              </div>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Deliverables
              </div>
              <ul className="space-y-2">
                {item.deliverables.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-2 text-zinc-200 text-sm"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 bg-[#E50914] shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Credits
              </div>
              <ul className="divide-y divide-white/10 border-t border-b border-white/10">
                {item.credits.map((c) => (
                  <li
                    key={c.role}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {c.role}
                    </span>
                    <span className="text-white text-sm">{c.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="flex items-end justify-between mb-14 gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-5">
              // Gallery
            </p>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl leading-[0.9]">
              Selected frames.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[280px] md:auto-rows-[260px] gap-4 md:gap-6">
          {item.gallery.map((src, i) => (
            <motion.div
              key={`${item.id}-g-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`case-study-gallery-${i}`}
              className={`portfolio-tile relative border border-white/10 bg-black overflow-hidden ${
                i === 0 ? "md:col-span-8 md:row-span-2" : i === 1 ? "md:col-span-4" : i === 2 ? "md:col-span-4" : "md:col-span-8"
              }`}
            >
              <img
                src={src}
                alt={`${item.title} frame ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 border-b border-white/10">
        <div className="flex items-end justify-between mb-14 gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-5">
              // More Work
            </p>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl leading-[0.9]">
              Keep exploring.
            </h2>
          </div>
          <Link
            to="/#portfolio"
            data-testid="case-study-all-work"
            className="inline-flex items-center gap-2 border border-white/20 hover:bg-white hover:text-black px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
          >
            All Work <CaretRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {related.map((r) => (
            <Link
              key={r.id}
              to={`/work/${r.id}`}
              data-testid={`related-case-${r.id}`}
              className="portfolio-tile group relative h-72 border border-white/10 hover:border-white/30 bg-black overflow-hidden"
            >
              <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-between p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80 bg-black/40 backdrop-blur-sm px-2 py-1 border border-white/10 w-fit">
                  {r.category}
                </span>
                <div className="flex items-end justify-between">
                  <h3 className="font-display uppercase text-white text-2xl md:text-3xl leading-none">
                    {r.title}
                  </h3>
                  <span className="w-10 h-10 bg-[#E50914] group-hover:bg-white flex items-center justify-center transition-colors">
                    <ArrowUpRight size={16} weight="bold" className="text-white group-hover:text-black" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-display uppercase text-white text-4xl sm:text-5xl md:text-6xl leading-[0.9]">
              Have a record <br /> that deserves this?
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link
              to="/#contact"
              data-testid="case-study-cta-contact"
              className="inline-flex items-center gap-3 bg-[#E50914] hover:bg-[#B9090B] text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Start a Project
              <ArrowUpRight size={14} weight="bold" />
            </Link>
            <a
              href={`tel:${STUDIO_INFO.phoneTel}`}
              data-testid="case-study-cta-call"
              className="inline-flex items-center gap-3 border border-white/25 hover:bg-white hover:text-black text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
            >
              Call {STUDIO_INFO.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0a0a0a] p-5 md:p-6">
      <div className="flex items-center gap-3 mb-3">
        <Icon size={16} weight="light" className="text-[#E50914]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {label}
        </span>
      </div>
      <div className="text-white text-base md:text-lg leading-tight">
        {value}
      </div>
    </div>
  );
}
